import { isNative, region } from 'utils/environment';
import LocalStorage, { StorageKeys } from 'utils/local-storage';
import logger from 'utils/logger';

import { AGE_GATE_THRESHOLDS } from './constants';
import { decideAgeGate } from './decide-age-gate';
import type {
  AgeGateDecision,
  AgeGatePlatformSignal,
  AgeSignalsStatus,
  SignificantChangeStatus,
} from './types';

const DECLINED_ERROR_CODES = new Set([
  'ERR_AGE_RANGE_USER_DECLINED',
  'ERR_AGE_RANGE_TASK_CANCELLED',
]);

const NOT_AVAILABLE_ERROR_CODES = new Set(['ERR_AGE_RANGE_NOT_AVAILABLE']);

type AgeRangeModule = typeof import('expo-age-range');

type AgeRangeModuleWithOptionalApis = AgeRangeModule & {
  isEligibleForAgeFeaturesAsync?: () => Promise<boolean>;
  requestAgeSignalsAccessAsync?: () => Promise<AgeSignalsStatus>;
};

type AgeRangeResponseWithOptionalStatuses = Awaited<
  ReturnType<AgeRangeModule['requestAgeRangeAsync']>
> & {
  significantChangeStatus?: SignificantChangeStatus | null;
  userStatus?: string | null;
};

function errorCode(error: unknown): string | undefined {
  if (error && typeof error === 'object' && 'code' in error) {
    return String((error as { code: unknown }).code);
  }
  return undefined;
}

function persistInstallId(installId: string | null | undefined) {
  if (installId) {
    LocalStorage.setItem(StorageKeys.AGE_GATE_ANDROID_INSTALL_ID, installId);
  }
}

function loadAgeRangeModule(): AgeRangeModule | null {
  try {
    // Lazy require so missing native binary (ExpoAgeRange) does not crash app import.
    return require('expo-age-range') as AgeRangeModule;
  } catch (error) {
    logger.warn({
      error,
      message: 'Age gate: expo-age-range native module unavailable; failing open',
    });
    return null;
  }
}

async function readAgeRulesApply(AgeRange: AgeRangeModuleWithOptionalApis): Promise<boolean | null> {
  if (!AgeRange.isEligibleForAgeFeaturesAsync) {
    return null;
  }

  try {
    return await AgeRange.isEligibleForAgeFeaturesAsync();
  } catch (error) {
    logger.warn({
      error,
      message: 'Age gate: isEligibleForAgeFeaturesAsync failed; treating as unknown',
    });
    return null;
  }
}

async function readAgeSignalsStatus(
  AgeRange: AgeRangeModuleWithOptionalApis
): Promise<AgeSignalsStatus | null> {
  if (!AgeRange.requestAgeSignalsAccessAsync) {
    return null;
  }

  try {
    return await AgeRange.requestAgeSignalsAccessAsync();
  } catch (error) {
    const code = errorCode(error);
    if (code && DECLINED_ERROR_CODES.has(code)) {
      return 'NOT_SHARED';
    }
    logger.warn({
      error,
      message: 'Age gate: requestAgeSignalsAccessAsync failed; treating as unknown',
    });
    return null;
  }
}

function getSignificantChangeStatus(
  ageRange: AgeRangeResponseWithOptionalStatuses
): SignificantChangeStatus | undefined {
  if (ageRange.significantChangeStatus) {
    return ageRange.significantChangeStatus;
  }

  return ageRange.userStatus === 'SUPERVISED_APPROVAL_DENIED' ? 'DECLINED' : undefined;
}

export async function evaluatePlatformAgeGate(): Promise<AgeGateDecision> {
  if (!isNative) {
    return 'allow';
  }

  const appRegion = region();
  if (appRegion === 'CA') {
    return 'allow';
  }

  let ageRulesApply: boolean | null = null;

  const decideWithoutRange = (
    signalState: AgeGatePlatformSignal['signalState'],
    overrides: Partial<AgeGatePlatformSignal> = {}
  ) =>
    decideAgeGate({
      region: appRegion,
      ageRulesApply,
      lowerBound: null,
      upperBound: null,
      ...overrides,
      signalState,
    });

  const AgeRange = loadAgeRangeModule();
  if (!AgeRange) {
    return decideWithoutRange('unsupported');
  }

  const AgeRangeCompat = AgeRange as AgeRangeModuleWithOptionalApis;

  try {
    ageRulesApply = await readAgeRulesApply(AgeRangeCompat);

    if (ageRulesApply === false) {
      return decideWithoutRange('ok');
    }

    const ageSignalsStatus = await readAgeSignalsStatus(AgeRangeCompat);

    // Android: only request range when SHARED. null (iOS) continues.
    if (ageSignalsStatus !== null && ageSignalsStatus !== 'SHARED') {
      return decideWithoutRange('ok', { ageSignalsStatus });
    }

    const ageRange = (await AgeRange.requestAgeRangeAsync({
      ...AGE_GATE_THRESHOLDS,
    })) as AgeRangeResponseWithOptionalStatuses;
    persistInstallId(ageRange.installId);

    return decideAgeGate({
      region: appRegion,
      ageRulesApply,
      lowerBound: ageRange.lowerBound ?? null,
      upperBound: ageRange.upperBound ?? null,
      ageSignalsStatus: ageSignalsStatus ?? undefined,
      significantChangeStatus: getSignificantChangeStatus(ageRange),
      signalState: 'ok',
    });
  } catch (error) {
    const code = errorCode(error);

    if (code && DECLINED_ERROR_CODES.has(code)) {
      return decideWithoutRange('declined');
    }

    if (code && NOT_AVAILABLE_ERROR_CODES.has(code)) {
      return decideWithoutRange('unknown');
    }

    logger.warn({
      error,
      message: 'Age gate: platform signal failed; failing open',
    });

    return decideWithoutRange('error');
  }
}
